import {
  ILegalStructureEdge,
  ILegalStructureGraphNode,
} from '../../../../services/queries/MaggieLegalStructureQueries';
import { nodeKey } from './useStructureChartGraph';
import { IStructureFamily } from './types';

/**
 * Fund families, as a band above their member funds (FD-4480).
 *
 * **Read from the raw payload, not from the chart's edges.** `MEMBERSHIP` is filtered out
 * of the drawn edges on purpose — the prototype's combined legend carries four line types
 * and membership is not among them, so a family is never joined to its funds by a
 * connector. The relationship still exists server-side and is still how membership is
 * recorded; it simply expresses grouping rather than a line, which is why this reads the
 * API edges directly instead of the filtered set the canvas renders.
 *
 * Kept separate from company lanes rather than reusing `laneId`. The two mean different
 * things and can co-occur: `deriveLanes` assigns a lane to any vehicle reaching exactly
 * one portfolio company, which a main fund can satisfy — so overloading one field would
 * let a fund's family band and its company lane overwrite each other.
 */
export type { IStructureFamily } from './types';

/** The `MEMBERSHIP` relationship, which only ever runs family → fund. */
const MEMBERSHIP = 'MEMBERSHIP';

export function deriveFamilyBands(
  nodes: ILegalStructureGraphNode[],
  edges: ILegalStructureEdge[]
): IStructureFamily[] {
  const familyNames = new Map<string, string>();
  nodes.forEach((node) => {
    if (node.ref.nodeType === 'family') {
      familyNames.set(nodeKey(node.ref.nodeType, node.ref.nodeId), node.name);
    }
  });

  if (familyNames.size === 0) {
    return [];
  }

  const membersByFamily = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (edge.relationshipType !== MEMBERSHIP || edge.parentType !== 'family') {
      return;
    }
    const familyId = nodeKey(edge.parentType, edge.parentId);
    // A membership edge naming a family the graph did not return is not an error — the
    // read is rooted, so a family outside the root's reach is simply absent. Grouping
    // funds under a band with no node would render a band with no heading.
    if (!familyNames.has(familyId)) {
      return;
    }
    const memberId = nodeKey(edge.childType, edge.childId);
    const existing = membersByFamily.get(familyId) ?? [];
    if (!existing.includes(memberId)) {
      existing.push(memberId);
    }
    membersByFamily.set(familyId, existing);
  });

  return (
    [...familyNames.entries()]
      .map(([id, name]) => ({ id, name, memberIds: membersByFamily.get(id) ?? [] }))
      // A family whose members are all outside the rooted read has nothing to group, and a
      // band around nothing is noise rather than information.
      .filter((family) => family.memberIds.length > 0)
  );
}

/**
 * Which family each fund belongs to.
 *
 * One family per fund is enforced server-side; where the data disagrees the first wins
 * and the rest are ignored rather than the fund appearing in two bands, which the layout
 * cannot express and which would double-count it in any per-band total.
 */
export function familyByMember(families: IStructureFamily[]): Map<string, string> {
  const byMember = new Map<string, string>();
  families.forEach((family) => {
    family.memberIds.forEach((memberId) => {
      if (!byMember.has(memberId)) {
        byMember.set(memberId, family.id);
      }
    });
  });

  return byMember;
}
